import React, { useState, useEffect, useCallback } from 'react';
import { Play, FileText, BarChart3, CheckCircle, Info, Wand2, CloudCog, Goal, Users, RefreshCw } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import api from '../api';
import toast from 'react-hot-toast';
import ProjectContextDisplay from '../components/ProjectContextDisplay';
import { useComplianceScore } from '../hooks/useComplianceScore';
import AnalysisResults from '../components/Analysis/AnalysisResults';
import ConfidenceScoringTab from '../components/Analysis/ConfidenceScoringTab';
import SOPGenerationTab from '../components/Analysis/SOPGenerationTab';
import Tooltip from '../components/Analysis/Tooltip';
import LoadingModal from '../components/LoadingModal';
import EnhancedProcessSynthesisTab from '../components/Analysis/EnhancedProcessSynthesisWrapper';


interface AnalysisProgress {
  progress: number;
  completed: number;
  failed: number;
  pending: number;
  analyzing: number;
  total: number;
}

const Analysis: React.FC = () => {
  const { currentProject } = useProject();
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('1a. Document Summaries');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Global compliance score management
  const { complianceScore, calculateComplianceScore } = useComplianceScore();

  
const refreshAnalysisData = async () => {
  try {
    await loadAnalysisResults();
    console.log('Analysis data refreshed successfully');
  } catch (error) {
    console.error('Failed to refresh analysis data:', error);
  }
};

  const refreshAnalysisDataWithFeedback = async () => {
    setIsRefreshing(true);
    try {
      await refreshAnalysisData();
      setTimeout(() => setIsRefreshing(false), 500);
    } catch (error) {
      setIsRefreshing(false);
      toast.error('Failed to refresh data');
    }
  };

  const loadAnalysisProgress = useCallback(async () => {
    if (!currentProject) return;
    try {
      const response = await api.get(`/api/analysis/progress/${currentProject.id}`);
      setAnalysisProgress(response.data);
    } catch (error) {
      console.error('Failed to load analysis progress');
    }
  }, [currentProject]);

 const loadAnalysisResults = useCallback(async () => {
  if (!currentProject) return;
  try {

    const timestamp = Date.now();
    const response = await api.get(`/api/analysis/results/${currentProject.id}?_t=${timestamp}`);
    
    console.log('Loaded fresh analysis results:', response.data);
    setAnalysisResults(response.data);

    // Calculate and update global compliance score
    const synthesisData = response.data.process_synthesis;
    calculateComplianceScore(synthesisData, response.data);
  } catch (error) {
    console.error('Failed to load analysis results:', error);
  }
}, [currentProject, calculateComplianceScore]);


  useEffect(() => {
    if (currentProject) {
      loadAnalysisProgress();
      loadAnalysisResults();
    }
  }, [currentProject]);

  // Poll for analysis results while analyzing
  useEffect(() => {
    let resultsInterval: NodeJS.Timeout | null = null;
    if (isAnalyzing) {
      resultsInterval = setInterval(() => {
        loadAnalysisResults();
      }, 3000);
    }
    return () => {
      if (resultsInterval) clearInterval(resultsInterval);
    };
  }, [isAnalyzing, loadAnalysisResults]);

  // Stop analyzing if all documents are completed or failed, and fetch results one last time
  useEffect(() => {
    if (isAnalyzing && analysisProgress) {
      const { completed, failed, total } = analysisProgress;
      if ((completed + failed === total) && total > 0) {
        setIsAnalyzing(false);
        loadAnalysisResults(); // Fetch results when analysis completes
      }
    }
  }, [isAnalyzing, analysisProgress, loadAnalysisResults]);

  // Polling for process_synthesis and SOP after analysis completes
  useEffect(() => {
    // Exit if analysis is running or if we already have everything.
    if (isAnalyzing || (analysisResults?.process_synthesis && analysisResults?.sop)) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 40; // 2 minutes timeout

    const intervalId = setInterval(() => {
      if (attempts >= maxAttempts) {
        console.warn("Post-analysis polling timed out.");
        clearInterval(intervalId);
        return;
      }

      // Polling for synthesis/SOP attempt
      loadAnalysisResults();
      attempts++;
    }, 3000);

    return () => clearInterval(intervalId);
  }, [isAnalyzing, analysisResults, loadAnalysisResults]);

  const startAnalysis = async (fullAnalysis = false) => {
    if (!currentProject) {
      toast.error('Please select a project first');
      return;
    }

    try {
      setIsAnalyzing(true);
      const response = await api.post(`/api/analysis/start/${currentProject.id}`, {
        fullAnalysis
      });
      toast.success(response.data.message);

      // Poll for progress updates
      const interval = setInterval(() => {
        loadAnalysisProgress();
      }, 3000);

      // Stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(interval);
        setIsAnalyzing(false);
      }, 600000);

    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to start analysis';
      toast.error(message);
      setIsAnalyzing(false);
    }
  };

  const reanalyzeCompleted = async () => {
    if (!currentProject) {
      toast.error('Please select a project first');
      return;
    }

    try {
      setIsAnalyzing(true);
      const response = await api.post(`/api/analysis/start/${currentProject.id}`, {
        fullAnalysis: false,
        reanalyzeCompleted: true
      });
      toast.success(response.data.message || 'Re-analyzing completed documents...');

      // Poll for progress updates
      const interval = setInterval(() => {
        loadAnalysisProgress();
      }, 3000);

      // Stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(interval);
        setIsAnalyzing(false);
      }, 600000);

    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to start re-analysis';
      toast.error(message);
      setIsAnalyzing(false);
    }
  };


  const tabs = [
    { name: '1a. Document Summaries', icon: FileText },
    { name: '1b. Process Synthesis', icon: BarChart3 }
  ];

  const hasDocumentsButNoAnalysis = () => {
    const documents = analysisResults?.documents || [];
    const analyses = analysisResults?.document_analyses || [];
    return documents.length > 0 && analyses.length === 0;
  };

  const hasAnalysisButNoSynthesis = () => {
    const analyses = analysisResults?.document_analyses || [];
    const synthesis = analysisResults?.process_synthesis;
    return analyses.length > 0 && !synthesis;
  };

  const hasAnalysisAndSynthesisButNoSop = () => {
    const synthesis = analysisResults?.process_synthesis;
    const sop = analysisResults?.sop;
    return synthesis && !sop;
  };

  
  return (
    <div className="space-y-6">
      {/* Add refresh indicator */}
      {isRefreshing && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-3 py-2 rounded-md shadow-lg z-50">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span className="text-sm">Updating...</span>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-super-dark-purple">Document Analysis Results</h1>
          <p className="text-gray-600">AI-powered analysis and synthesis for Current Project</p>
        </div>
        <div className="flex flex-col space-y-3">
          <div className="flex space-x-3">
            <Tooltip
              text={`Incremental Analysis (Faster)

• Only processes documents with "pending" status
• Keeps existing analysis results intact
• Faster execution for new documents only
• Best for: Adding new docs to existing project

Perfect when you've uploaded new documents and want to analyze just those without redoing previous work.`}
              placement="bottom"
            >
              <button
                data-tour="analyze-button"
                onClick={() => startAnalysis(false)}
                disabled={isAnalyzing || (analysisProgress?.pending ?? 0) <= 0}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isAnalyzing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span>{isAnalyzing ? 'Analyzing...' : 'Analyze New Documents'}</span>
              </button>
            </Tooltip>

            <Tooltip
              text={`Re-analyze Completed Documents (Cache Testing)

• Re-analyzes documents that are already "completed"
• Does NOT delete existing results
• Perfect for testing LLM cache reproducibility
• Uses cached responses when same content is analyzed
• Best for: Cache testing and verification

Use this to verify that same documents produce identical results (reproducibility testing). If cache is working, results should be instant and identical.`}
              placement="bottom"
            >
              <button
                onClick={reanalyzeCompleted}
                disabled={isAnalyzing || (analysisProgress?.completed ?? 0) <= 0}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isAnalyzing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span>{isAnalyzing ? 'Re-analyzing...' : 'Re-analyze Completed'}</span>
              </button>
            </Tooltip>

            <Tooltip
              text={`Full Analysis (Complete Regeneration)

• Resets ALL documents to "analyzing" status
• Deletes all existing analysis results
• Reprocesses every document from scratch
• Regenerates synthesis, SOPs, risk assessments, etc.
• Incorporates ALL documents including new ones
• Best for: When you want everything regenerated

Use this when you've added new documents and want the entire analysis to be rebuilt considering all information together. Takes longer but ensures comprehensive integration.`}
              placement="bottom"
            >
              <button
                onClick={() => startAnalysis(true)}
                disabled={isAnalyzing || (analysisProgress?.completed ?? 0) <= 0}
                className="bg-electric-blue text-white px-4 py-2 rounded-lg hover:bg-darker-blue transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isAnalyzing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <Wand2 className="w-4 h-4" />
                )}
                <span>{isAnalyzing ? 'Re-analyzing All...' : 'Re-analyze Everything'}</span>
              </button>
            </Tooltip>

          </div>

          {analysisProgress && analysisProgress.analyzing > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                <span className="text-orange-800 text-sm font-medium">
                  Re-analyzing documents to incorporate all information...
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Project Context Display */}
      <ProjectContextDisplay />

      {analysisProgress && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Analysis Progress</h3>
            <span className="text-sm text-gray-600">
              {analysisProgress.completed}/{analysisProgress.total} documents completed
              {analysisProgress.analyzing > 0 && ` • ${analysisProgress.analyzing} analyzing`}
            </span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-electric-blue h-2 rounded-full transition-all duration-300"
              style={{ width: `${analysisProgress.progress}%` }}
            ></div>
          </div>

          <div className="grid grid-cols-5 gap-4 text-center text-sm">
            <div>
              <div className="text-2xl font-bold text-green-600">{analysisProgress.completed}</div>
              <div className="text-gray-600">Completed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{analysisProgress.analyzing || 0}</div>
              <div className="text-gray-600">Analyzing</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{analysisProgress.pending}</div>
              <div className="text-gray-600">Pending</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{analysisProgress.failed}</div>
              <div className="text-gray-600">Failed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-600">{analysisProgress.total}</div>
              <div className="text-gray-600">Total</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2 ${activeTab === tab.name
                    ? 'border-electric-blue text-electric-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === '1a. Document Summaries' && (
            <AnalysisResults
              analysisResults={analysisResults}
              isAnalyzing={isAnalyzing}
              analysisProgress={analysisProgress}
              setActiveTab={setActiveTab}
               onStartAnalysis={startAnalysis}
            />
          )}
          {/* {activeTab === '1b. Process Synthesis' && (
            <ProcessSynthesisTab
              analysisResults={analysisResults}
              isAnalyzing={isAnalyzing}
              analysisProgress={analysisProgress}
              globalComplianceScore={complianceScore}
              onRefresh={refreshAnalysisDataWithFeedback}
            />
          )} */}
          {activeTab === '1b. Process Synthesis' && (
            <EnhancedProcessSynthesisTab
              analysisResults={analysisResults}
              isAnalyzing={isAnalyzing}
              analysisProgress={analysisProgress}
              globalComplianceScore={complianceScore}
              onRefresh={refreshAnalysisDataWithFeedback}
            />
          )}
        </div>
      </div>

    </div>
  );
};

export default Analysis;